/**
 * Operation Event Detail Modal
 *
 * Drill-in pane opened when an admin clicks a row in the Scheduled Items
 * Overview timeline. Surfaces three things in one place so an operator
 * can investigate and recover without context switching:
 *
 *   1. Event header — kind, timestamp, status, customer/contact/recipient
 *      first names, event type. Mirrors what the row already shows so the
 *      operator can confirm they opened the right entry. Last names and
 *      sensitive content (e-card body, flower delivery date) are deliberately
 *      absent — see `AdminOperationEventDto` privacy contract.
 *   2. Audit feed — the per-entity audit-event slice from
 *      `/admin/audit-events?entityType=ScheduledItem|FlowerOrder&entityId=...`,
 *      and for e-cards the parallel `entityType=ScheduledCard&entityId=...`
 *      query so the email pipeline rows surface alongside the parent
 *      `ScheduledItem` rows. Two parallel calls is the simplest way to keep
 *      the existing audit query API unchanged.
 *   3. Recovery actions — admin Send Now / Cancel (e-cards) and Place Order Now
 *      (flower pre-orders), wired to the dedicated `/admin/operations/*`
 *      endpoints. The flower action also offers a deep-link button into the
 *      existing `AdminFlowerOrderDetailModal` for the full PII drill-in.
 *
 * Nested admin modals (`CustomerDetailModal`, `AdminFlowerOrderDetailModal`)
 * mount only while open so they cannot recursively remount this modal through
 * the customer → smiles → ops detail chain (browser OOM on /admin/customers).
 * They each fire their own audited reads, so operator access to PII is logged
 * exactly once, the same way it would be from the existing admin pages.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconExternalLink,
  IconMail,
  IconMailbox,
  IconRefresh,
  IconUser,
  IconFlower,
  IconTruckDelivery,
  IconGift,
  IconX,
} from '@tabler/icons-react';
import { AppModal } from '../common/AppModal';
import { useModalZIndex } from '../../hooks/useModalZIndex';
import { AuditEventsTable } from './AuditEventsTable';
import type { AuditEvent, AuditEventQueryResult } from './AuditEventsTable';
import { AdminEcardThumbnail } from './AdminEcardThumbnail';
import { CustomerDetailModal } from './CustomerDetailModal';
import { AdminFlowerOrderDetailModal } from './AdminFlowerOrderDetailModal';
import { AdminMailedCardOrderDetailModal } from './AdminMailedCardOrderDetailModal';
import { AdminGiftOrderDetailModal } from './AdminGiftOrderDetailModal';
import apiClient from '../../services/apiClient';
import {
  cancelCardAsAdmin,
  placeFlowerOrderAsAdmin,
  placeGiftOrderAsAdmin,
  sendCardAsAdmin,
  OPERATION_EVENT_KIND,
  type AdminOperationEvent,
} from '../../services/adminOperationsService';
import { actionColors, brandColors, semanticColors } from '../../config/theme';
import { formatInstantLocal, compareApiUtcInstants } from '../../utils/formatDateTime';
import { ScheduledItemStatus, FulfillmentStatus } from '../../types';
import {
  canAdminPlaceFlowerOrder,
  formatCombinedOrderStatusLabel,
  formatStatusLabel,
  getOperationEventStatusColor,
  parseCombinedOrderStatus,
} from '../../utils/orderStatusDisplay';

/**
 * Backend `AuditEntityTypes` constants we filter the audit feed on. Repeated
 * here as string literals because the frontend has no auto-generated enum
 * mirror; the backend uses these exact values when stamping audit rows.
 */
const AUDIT_ENTITY_SCHEDULED_ITEM = 'ScheduledItem';
const AUDIT_ENTITY_SCHEDULED_CARD = 'ScheduledCard';
const AUDIT_ENTITY_FLOWER_ORDER = 'FlowerOrder';

/** Audit entity type for mailed-card placement events (PBI #1222). */
const AUDIT_ENTITY_MAILED_CARD_ORDER = 'MailedCardOrder';
const AUDIT_ENTITY_GIFT_ORDER = 'GiftOrder';

/** Modal title prefix per timeline row kind. */
const DETAIL_TITLE_BY_KIND: Record<string, string> = {
  [OPERATION_EVENT_KIND.Email]: 'E-Card',
  [OPERATION_EVENT_KIND.FlowerOrder]: 'Flower Order',
  [OPERATION_EVENT_KIND.MailedCardOrder]: 'Mailed Card Order',
  [OPERATION_EVENT_KIND.GiftOrder]: 'Gift Order',
};

/** Page size for the per-event audit feed. Matches the page-level default. */
const AUDIT_PAGE_SIZE = 50;

/**
 * Props for the operation detail modal. The `event` is non-null when the
 * modal is open; passing `null` keeps the modal rendered but inert so the
 * exit transition can play out.
 */
export interface OperationEventDetailModalProps {
  /** Event row clicked in the timeline; null while modal is closed. */
  event: AdminOperationEvent | null;
  /** Whether the modal is open. */
  opened: boolean;
  /** Close handler. */
  onClose: () => void;
  /** Called after a recovery action so the page can refresh its list. */
  onActionApplied?: () => void;
}

/**
 * Resolves the badge color for e-card statuses or combined flower
 * `Fulfillment/Payment` strings from the operations timeline.
 */
const ECARD_STATUS_COLORS: Record<string, string> = {
  [ScheduledItemStatus.Pending]: 'blue',
  [ScheduledItemStatus.Failed]: 'red',
  [ScheduledItemStatus.Cancelled]: 'gray',
  [ScheduledItemStatus.Sent]: 'green',
  [ScheduledItemStatus.OrderPlaced]: 'green',
};

/** Formats a status string for display (e-card or combined flower axes). */
const formatEventStatusLabel = (status: string): string =>
  status.includes('/')
    ? formatCombinedOrderStatusLabel(status)
    : formatStatusLabel(status);

/** Renders an ISO timestamp in the operator's local timezone. */
const formatDateTime = (iso: string): string => formatInstantLocal(iso);

/** Composes the recipient label depending on event kind. */
const getRecipientLabel = (event: AdminOperationEvent): string | null => {
  if (event.kind === OPERATION_EVENT_KIND.Email) {
    return event.contactFirstName ?? null;
  }
  return event.recipientFirstName ?? null;
};

/** Returns the leading icon for the kind badge. */
const getKindIcon = (kind: string) => {
  if (kind === OPERATION_EVENT_KIND.Email) {
    return <IconMail size={14} />;
  }

  if (kind === OPERATION_EVENT_KIND.MailedCardOrder) {
    return <IconMailbox size={14} />;
  }

  if (kind === OPERATION_EVENT_KIND.GiftOrder) {
    return <IconGift size={14} />;
  }

  return <IconFlower size={14} />;
};

/**
 * Builds an absolute URL with the audit-event filter for this row's entity.
 * Calling code stays thin — the browser network panel and Vitest mocks see a
 * normal `apiClient.get` invocation.
 */
const buildAuditQueryUrl = (entityType: string, entityId: number): string => {
  const params = new URLSearchParams();
  params.append('entityType', entityType);
  params.append('entityId', String(entityId));
  params.append('pageSize', String(AUDIT_PAGE_SIZE));
  return `/admin/audit-events?${params.toString()}`;
};

export const OperationEventDetailModal = ({
  event,
  opened,
  onClose,
  onActionApplied,
}: OperationEventDetailModalProps) => {
  const zIndex = useModalZIndex(opened);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [flowerModalOpen, setFlowerModalOpen] = useState(false);
  const [mailedCardModalOpen, setMailedCardModalOpen] = useState(false);
  const [giftModalOpen, setGiftModalOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  /** Optimistic status after Send/Cancel so the badge updates before the parent list refreshes. */
  const [statusOverride, setStatusOverride] = useState<string | null>(null);
  const cancelConfirmZIndex = useModalZIndex(cancelConfirmOpen);

  /**
   * Loads the audit feed for the current event. For e-cards we pull the
   * `ScheduledItem` and `ScheduledCard` slices in parallel and merge by
   * timestamp so the email send rows show up alongside their parent
   * scheduled-item lifecycle rows. For flower orders we just pull the
   * `FlowerOrder` slice — the placement audit chain is rooted there.
   */
  const loadAuditEvents = useCallback(async () => {
    if (!event) {
      setAuditEvents([]);
      return;
    }

    setAuditLoading(true);
    setAuditError(null);

    try {
      const requests: Array<Promise<{ data: AuditEventQueryResult }>> = [];

      if (event.kind === OPERATION_EVENT_KIND.Email) {
        requests.push(
          apiClient.get<AuditEventQueryResult>(
            buildAuditQueryUrl(AUDIT_ENTITY_SCHEDULED_ITEM, event.scheduledItemId)
          )
        );
        if (event.cardId != null) {
          requests.push(
            apiClient.get<AuditEventQueryResult>(
              buildAuditQueryUrl(AUDIT_ENTITY_SCHEDULED_CARD, event.cardId)
            )
          );
        }
      } else if (event.flowerOrderId != null) {
        requests.push(
          apiClient.get<AuditEventQueryResult>(
            buildAuditQueryUrl(AUDIT_ENTITY_FLOWER_ORDER, event.flowerOrderId)
          )
        );
      } else if (event.mailedCardOrderId != null) {
        requests.push(
          apiClient.get<AuditEventQueryResult>(
            buildAuditQueryUrl(AUDIT_ENTITY_MAILED_CARD_ORDER, event.mailedCardOrderId)
          )
        );
      } else if (event.giftOrderId != null) {
        requests.push(
          apiClient.get<AuditEventQueryResult>(
            buildAuditQueryUrl(AUDIT_ENTITY_GIFT_ORDER, event.giftOrderId)
          )
        );
      }

      const responses = await Promise.all(requests);
      const merged = responses
        .flatMap((response) => response.data.items)
        .sort(
          (a, b) =>
            compareApiUtcInstants(b.occurredAtUtc, a.occurredAtUtc)
        );

      setAuditEvents(merged);
    } catch (err) {
      console.error('Failed to load audit events for operation', err);
      setAuditError('Failed to load audit events for this entry.');
    } finally {
      setAuditLoading(false);
    }
  }, [event]);

  useEffect(() => {
    if (!opened || !event) {
      setAuditEvents([]);
      setAuditError(null);
      setStatusOverride(null);
      return;
    }
    setStatusOverride(null);
    loadAuditEvents();
  }, [opened, event, loadAuditEvents]);

  /** Resets all transient modal state when closed. */
  const handleClose = () => {
    setCustomerModalOpen(false);
    setFlowerModalOpen(false);
    setCancelConfirmOpen(false);
    setStatusOverride(null);
    onClose();
  };

  /** Send Now action for e-card events. Refreshes audit + parent on success. */
  const handleSendCard = async () => {
    if (!event || event.cardId == null) return;
    setActionLoading(true);

    try {
      await sendCardAsAdmin(event.cardId);
      notifications.show({
        title: 'Card sent',
        message: `Card #${event.cardId} sent successfully.`,
        color: brandColors.success,
      });
      setStatusOverride(ScheduledItemStatus.Sent);
      onActionApplied?.();
      await loadAuditEvents();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Failed to send card.';
      notifications.show({
        title: 'Send failed',
        message,
        color: brandColors.danger,
      });
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Admin cancel for a pending/failed e-card. Same eligibility as Send Now
   * (anything except Sent/Cancelled); confirmation lives in a stacked AppModal
   * so the CTA stays reachable on mobile.
   */
  const handleCancelCard = async () => {
    if (!event || event.cardId == null) return;
    setActionLoading(true);

    try {
      await cancelCardAsAdmin(event.cardId);
      notifications.show({
        title: 'Card cancelled',
        message: `Card #${event.cardId} cancelled on behalf of the customer.`,
        color: brandColors.success,
      });
      setStatusOverride(ScheduledItemStatus.Cancelled);
      setCancelConfirmOpen(false);
      onActionApplied?.();
      await loadAuditEvents();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Failed to cancel card.';
      notifications.show({
        title: 'Cancel failed',
        message,
        color: brandColors.danger,
      });
    } finally {
      setActionLoading(false);
    }
  };

  /** Place Order Now action for flower events. */
  const handlePlaceFlowerOrder = async () => {
    if (!event || event.flowerOrderId == null) return;
    setActionLoading(true);

    try {
      const response = await placeFlowerOrderAsAdmin(event.flowerOrderId);
      notifications.show({
        title: 'Order placed',
        message: response.message,
        color: brandColors.success,
      });
      onActionApplied?.();
      await loadAuditEvents();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Failed to place order.';
      notifications.show({
        title: 'Place order failed',
        message,
        color: brandColors.danger,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePlaceGiftOrder = async () => {
    if (!event || event.giftOrderId == null) return;
    setActionLoading(true);

    try {
      const response = await placeGiftOrderAsAdmin(event.giftOrderId);
      notifications.show({
        title: 'Order placed',
        message: response.message,
        color: brandColors.success,
      });
      onActionApplied?.();
      await loadAuditEvents();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Failed to place gift order.';
      notifications.show({
        title: 'Place order failed',
        message,
        color: brandColors.danger,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const displayStatus = statusOverride ?? event?.status ?? '';

  /**
   * Whether an admin Send Now is allowed for this e-card. Only Pending and
   * Failed scheduled items can be re-sent — the controller rejects others
   * and the user-visible button shouldn't tempt the operator into a 400.
   */
  const canSendCard =
    event?.kind === OPERATION_EVENT_KIND.Email &&
    event.cardId != null &&
    (displayStatus === ScheduledItemStatus.Pending ||
      displayStatus === ScheduledItemStatus.Failed);

  /**
   * Whether an admin cancel is allowed. Matches the user cancel endpoint:
   * anything except Sent (and already Cancelled) can be cancelled.
   */
  const canCancelCard =
    event?.kind === OPERATION_EVENT_KIND.Email &&
    event.cardId != null &&
    displayStatus !== ScheduledItemStatus.Sent &&
    displayStatus !== ScheduledItemStatus.Cancelled;

  /**
   * Whether an admin Place Order Now is allowed. Only Pending and
   * PaymentAuthorized flower pre-orders are eligible — see the three
   * pre-flight guards in `AdminOperationsController.PlaceFlowerOrder`.
   * The 30-day forward-window guard runs server-side; the button stays
   * visible for those rows so the operator gets a clear rejection message
   * instead of silently disappearing UI.
   */
  const canPlaceFlower =
    event?.kind === OPERATION_EVENT_KIND.FlowerOrder
    && event.flowerOrderId != null
    && (() => {
      const axes = parseCombinedOrderStatus(event.status);
      return axes != null && canAdminPlaceFlowerOrder(axes.fulfillmentStatus, axes.paymentStatus);
    })();

  const canPlaceGift =
    event?.kind === OPERATION_EVENT_KIND.GiftOrder
    && event.giftOrderId != null
    && !event.VendorOrderId
    && (() => {
      const axes = parseCombinedOrderStatus(event.status);
      return axes != null && axes.fulfillmentStatus !== FulfillmentStatus.Cancelled;
    })();

  const footer = event ? (
    <Group justify="space-between" wrap="wrap">
      <Button
        variant="light"
        leftSection={<IconUser size={16} />}
        onClick={() => setCustomerModalOpen(true)}
      >
        Open Customer
      </Button>
      <Group gap="xs" wrap="wrap">
        {canCancelCard && (
          <Button
            color={actionColors.cancel}
            variant="light"
            leftSection={<IconX size={16} />}
            onClick={() => setCancelConfirmOpen(true)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
        )}
        {canSendCard && (
          <Button
            color={actionColors.sendNow}
            leftSection={<IconMail size={16} />}
            onClick={handleSendCard}
            loading={actionLoading}
          >
            Send Now
          </Button>
        )}
        {canPlaceFlower && (
          <Button
            color={actionColors.sendNow}
            leftSection={<IconTruckDelivery size={16} />}
            onClick={handlePlaceFlowerOrder}
            loading={actionLoading}
          >
            Place Order Now
          </Button>
        )}
        {canPlaceGift && (
          <Button
            color={actionColors.sendNow}
            leftSection={<IconGift size={16} />}
            onClick={handlePlaceGiftOrder}
            loading={actionLoading}
          >
            Place Order Now
          </Button>
        )}
        {event.kind === OPERATION_EVENT_KIND.FlowerOrder &&
          event.flowerOrderId != null && (
            <Button
              variant="light"
              leftSection={<IconExternalLink size={16} />}
              onClick={() => setFlowerModalOpen(true)}
            >
              Order Details
            </Button>
          )}
        {event.kind === OPERATION_EVENT_KIND.MailedCardOrder &&
          event.mailedCardOrderId != null && (
            <Button
              variant="light"
              leftSection={<IconExternalLink size={16} />}
              onClick={() => setMailedCardModalOpen(true)}
            >
              Order Details
            </Button>
          )}
        {event.kind === OPERATION_EVENT_KIND.GiftOrder &&
          event.giftOrderId != null && (
            <Button
              variant="light"
              leftSection={<IconExternalLink size={16} />}
              onClick={() => setGiftModalOpen(true)}
            >
              Order Details
            </Button>
          )}
      </Group>
    </Group>
  ) : null;

  return (
    <>
      <AppModal
        id="operation-event-detail"
        opened={opened}
        onClose={handleClose}
        zIndex={zIndex}
        title={
          event
            ? `${DETAIL_TITLE_BY_KIND[event.kind] ?? 'Scheduled Item'} #${
                event.flowerOrderId
                  ?? event.mailedCardOrderId
                  ?? event.giftOrderId
                  ?? event.cardId
                  ?? event.scheduledItemId
              }`
            : 'Scheduled Item'
        }
        size="90%"
        footer={footer}
      >
        {event ? (
          <Stack gap="md">
            {/*
              Thumbnail row sits ABOVE the badges so a 120x68 image doesn't
              recenter the badges via Group's default vertical alignment.
              Only renders when there is something to show — the bare badges
              row otherwise behaves exactly as before.
            */}
            {(event.thumbnailHidden ||
              event.thumbnailMediaToken ||
              event.thumbnailExternalUrl) && (
              <AdminEcardThumbnail
                hidden={event.thumbnailHidden}
                mediaToken={event.thumbnailMediaToken}
                externalUrl={event.thumbnailExternalUrl}
                isVideo={event.thumbnailIsVideo}
                width={120}
                height={68}
                alt={
                  event.kind === OPERATION_EVENT_KIND.FlowerOrder
                    ? 'Product preview'
                    : 'Card preview'
                }
                fallback={
                  <ThemeIcon size={68} variant="light" color="gray" radius="md">
                    <IconMail size={32} />
                  </ThemeIcon>
                }
              />
            )}

            <Group gap="sm" wrap="wrap">
              <Badge
                leftSection={getKindIcon(event.kind)}
                color={
                  event.kind === OPERATION_EVENT_KIND.Email ? 'blue' : 'pink'
                }
                variant="light"
                size="lg"
              >
                {event.kind === OPERATION_EVENT_KIND.Email
                  ? 'E-Card'
                  : 'Flower Order'}
              </Badge>
              <Badge
                color={getOperationEventStatusColor(displayStatus, ECARD_STATUS_COLORS)}
                variant="light"
                size="lg"
              >
                {formatEventStatusLabel(displayStatus)}
              </Badge>
              {event.VendorOrderId && (
                <Badge color="cyan" variant="light" size="lg">
                  F1 #{event.VendorOrderId}
                </Badge>
              )}
            </Group>

            <Box>
              <Group gap="lg" wrap="wrap">
                <Stack gap={2}>
                  <Text size="xs" c={semanticColors.text.secondary}>
                    Scheduled
                  </Text>
                  <Text size="sm">{formatDateTime(event.eventTimestamp)}</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c={semanticColors.text.secondary}>
                    Event type
                  </Text>
                  <Text size="sm">{event.eventTypeName}</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c={semanticColors.text.secondary}>
                    Customer
                  </Text>
                  <Text size="sm">{event.userFirstName || '--'}</Text>
                </Stack>
                {getRecipientLabel(event) && (
                  <Stack gap={2}>
                    <Text size="xs" c={semanticColors.text.secondary}>
                      {event.kind === OPERATION_EVENT_KIND.Email
                        ? 'Contact'
                        : 'Recipient'}
                    </Text>
                    <Text size="sm">{getRecipientLabel(event)}</Text>
                  </Stack>
                )}
              </Group>
            </Box>

            <Divider label="Audit feed" labelPosition="left" />

            <Group justify="space-between">
              <Text size="sm" c={semanticColors.text.secondary}>
                Showing audit events for this {event.kind === OPERATION_EVENT_KIND.Email ? 'e-card' : 'flower order'}.
              </Text>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={loadAuditEvents}
                loading={auditLoading}
              >
                Refresh
              </Button>
            </Group>

            {auditError && (
              <Alert
                color={brandColors.danger}
                icon={<IconAlertCircle size={16} />}
              >
                {auditError}
              </Alert>
            )}

            {auditLoading ? (
              <Center py="xl">
                <Loader />
              </Center>
            ) : (
              <AuditEventsTable
                events={auditEvents}
                loading={false}
                error={null}
                totalCount={auditEvents.length}
                hasMore={false}
                isTruncated={false}
              />
            )}
          </Stack>
        ) : null}
      </AppModal>

      {customerModalOpen ? (
        <CustomerDetailModal
          customerId={event?.userId ?? null}
          opened={customerModalOpen}
          onClose={() => setCustomerModalOpen(false)}
        />
      ) : null}

      {flowerModalOpen ? (
        <AdminFlowerOrderDetailModal
          orderId={event?.flowerOrderId ?? null}
          opened={flowerModalOpen}
          onClose={() => setFlowerModalOpen(false)}
          onOrderUpdated={() => {
            onActionApplied?.();
            loadAuditEvents();
          }}
        />
      ) : null}

      {mailedCardModalOpen ? (
        <AdminMailedCardOrderDetailModal
          orderId={event?.mailedCardOrderId ?? null}
          opened={mailedCardModalOpen}
          onClose={() => setMailedCardModalOpen(false)}
          onOrderUpdated={() => {
            onActionApplied?.();
            loadAuditEvents();
          }}
        />
      ) : null}

      {giftModalOpen ? (
        <AdminGiftOrderDetailModal
          orderId={event?.giftOrderId ?? null}
          opened={giftModalOpen}
          onClose={() => setGiftModalOpen(false)}
          onOrderUpdated={() => {
            onActionApplied?.();
            loadAuditEvents();
          }}
        />
      ) : null}

      <AppModal
        id="admin-operation-ecard-cancel-confirm"
        opened={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        zIndex={cancelConfirmZIndex}
        title="Confirm Cancel E-Card"
        bodyHeight="auto"
        footer={
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setCancelConfirmOpen(false)}
              disabled={actionLoading}
            >
              Keep Card
            </Button>
            <Button
              color={actionColors.cancel}
              onClick={handleCancelCard}
              loading={actionLoading}
            >
              Yes, Cancel Card
            </Button>
          </Group>
        }
      >
        <Text size="sm">
          Cancel this e-card on behalf of the customer? It will move to Cancelled
          and will not send on the scheduled date.
        </Text>
      </AppModal>
    </>
  );
};
